<template>
  <div
    class="overflow-hidden rounded-md bg-white shadow-dropdown-dialog"
    :class="{ 'w-400px': props.layoutType === 'desktop' }"
  >
    <div
      ref="list"
      class="relative grow-0 overflow-auto"
      :style="`max-height: ${showCount * 96}px`"
      @scroll="handleScroll"
    >
      <div v-for="(item, index) in cartItems" :key="index">
        <div
          class="flex cursor-pointer items-center border-b border-b-lightGray px-8px py-12px hover:bg-light-gray"
          :class="{ 'opacity-50 pointer-events-none': deletedLineIds.has(item.lineId) }"
          @click="redirectTo(item.url)"
        >
          <img
            v-if="item.imageUrl !== null"
            alt="image"
            class="mr-4px"
            :src="item.imageUrl"
            width="72px"
          />
          <div class="mr-4px w-full flex flex-col">
            <div class="line-clamp-2 mb-4px content-12-h16-b text-primary-80">
              {{ item.product }}
            </div>
            <span class="mb-4px truncate content-11-h16-r text-primary-80">{{ item.color }}</span>
            <div class="flex items-center">
              <span
                v-if="item.compareAtPrice !== null"
                class="mr-8px content-12-h16-b text-primary-40 line-through"
                >{{ priceWithLocaleCurrencyUnit(item.compareAtPrice) }}</span
              >
              <span
                class="content-12-h16-b"
                :class="item.compareAtPrice !== null ? 'text-red' : 'text-amber'"
                >{{ priceWithLocaleCurrencyUnit(item.price) }}</span
              >
            </div>
          </div>
          <span
            class="mr-4px inline-block shrink-0 whitespace-nowrap content-12-h16-b text-primary-80"
          >
            X {{ item.quantity }}</span
          >
          <div
            class="h-40px w-40px flex items-center justify-center rounded-full transition-colors duration-200 hover:bg-green-transparent-10"
            @click.stop="() => removeCartItem(item.lineId)"
          >
            <LoadingSpinner
              v-if="deletedLineIds.has(item.lineId)"
              class="col-span-full mx-auto"
              is-loading
            />
            <i v-else class="icon-trash mx-8px text-24px text-primary-80" />
          </div>
        </div>
      </div>
      <div
        v-if="cartItems.length > showCount && isAtTop"
        class="pointer-events-none absolute bottom-0 left-0 h-1/4 w-full bg-[linear-gradient(to_top,_white_30%,_transparent_100%)]"
      ></div>
    </div>
    <div class="h-56px flex items-center justify-between">
      <div class="ml-24px">
        <p class="content-10-h12-r text-primary-80">
          {{ $t('cart-preview.number-of-items', { count: cartItems.length }) }}
        </p>
        <span class="flex items-center">
          <p class="mr-4px content-10-h12-r text-primary-80">
            {{ $t('cart-preview.total-amount') }}
          </p>
          <p class="content-12-h16-b text-amber">
            {{ priceWithLocaleCurrencyUnit(totalAmount) }}
          </p></span
        >
      </div>
      <AuButton
        class="mr-12px h-40px flex-shrink-0 px-24px py-12px"
        type="fill"
        @click="
          () => {
            pushGA4ViewCart()
            redirectOfficialCartPage()
          }
        "
      >
        {{ $t('btn.go-to-cart') }}
      </AuButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AuButton, LoadingSpinner } from '@evolutivelabs/amuse-ui'
import { type MoneyV2 } from '@evolutivelabs/rhinoshield-shopify-client'
import { computed, onMounted, ref, useTemplateRef } from 'vue'

import { priceWithLocaleCurrencyUnit } from '@/locale'
import { useCartStore } from '@/store/cartStore'
import { type LayoutType } from '@/store/layoutStore'
import { pushGA4ViewCart } from '@/utils/googleAnalytics'
import { redirectOfficialCartPage } from '@/utils/redirect'
import { isNotNull } from '@/utils/type.util'

const props = defineProps<{
  layoutType: LayoutType
}>()
const cartStore = useCartStore()
const showCount = computed(() => (props.layoutType === 'mobile' ? 3 : 5))
const totalAmount = computed(() => Number(cartStore.cart?.cost.totalAmount.amount))
const cartItems = computed(() => {
  return (
    cartStore.cart?.rhinoLines
      .map((x) => {
        if (x.rhinoType === 'free-gift') return null
        const lineId = x.id
        const imageUrl = x.rhinoAttribute._ecommerce?.image ?? x.merchandise.image?.url ?? null
        const quantity = x.quantity
        const product = x.merchandise.product.title
        const color = x.rhinoAttribute._ecommerce?.variantTitle
        const url = x.rhinoAttribute._url
        const { price, compareAtPrice } =
          x.rhinoType === 'design-creator-product'
            ? getDisplayPrice(
                x.rhinoBundlePrice,
                x.rhinoBundleCompareAtPrice,
                x.discountAllocations,
              )
            : getDisplayPrice(
                x.merchandise.price,
                x.merchandise.compareAtPrice ?? null,
                x.discountAllocations,
              )
        return { lineId, product, imageUrl, url, quantity, color, compareAtPrice, price }
      })
      .filter(isNotNull) ?? []
  )
})

const deletedLineIds = cartStore.deletedLineIds
async function removeCartItem(lineId: string): Promise<void> {
  await cartStore.removeRhinoLineFromCart(lineId)
}

function getDisplayPrice(
  price: MoneyV2,
  compareAtPrice: MoneyV2 | null,
  discounts: Array<{ discountedAmount: MoneyV2 }>,
): { compareAtPrice: null | number; price: number } {
  // issue: https://gitlab.com/evolutivelabs/amuse/rhinoshield-shopify-client/-/issues/12
  const discountedAmount = discounts.reduce(
    (sum, current) => sum + Number(current.discountedAmount.amount),
    0,
  )
  return {
    price: Number(price.amount) - discountedAmount,
    compareAtPrice:
      compareAtPrice === null
        ? discountedAmount > 0
          ? Number(price.amount)
          : null
        : Number(compareAtPrice.amount) - discountedAmount,
  }
}

const listRef = useTemplateRef<HTMLElement>('list')
const isAtTop = ref(true)

function handleScroll(): void {
  const scrollTop = listRef.value?.scrollTop ?? 0
  isAtTop.value = scrollTop === 0
}

function redirectTo(url: string): void {
  window.location.href = url
}
onMounted(() => {
  handleScroll()
})
</script>
